/**
 * Badge -- status indicator pill with Cohere 2026 design tokens.
 *
 * Supports 6 status variants (pending, running, complete, failed, waiting, paused)
 * with mapped color/border/background tokens from specs/integration-plan.md Section 3.3.
 *
 * BadgeByKind is a backwards-compatible alias that maps `kind` to `status`.
 *
 * Applies .motion__status-fade for one render tick after the status prop
 * changes (FR-011 / T304). The animation is ~16ms and handled by motion.css.
 */
import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status?: "pending" | "running" | "complete" | "failed" | "waiting" | "paused";
  icon?: IconName;
  children?: ReactNode;
}

const STATUS_STYLES: Record<
  NonNullable<BadgeProps["status"]>,
  { color: string; border: string; background: string }
> = {
  pending: {
    color: "var(--fg3)",
    border: "var(--border-hairline)",
    background: "var(--bg1)",
  },
  running: {
    color: "var(--color-action-blue)",
    border: "1px solid var(--color-action-blue)",
    background: "var(--bg3)",
  },
  complete: {
    color: "var(--color-deep-green)",
    border: "1px solid var(--color-deep-green)",
    background: "var(--bg4)",
  },
  failed: {
    color: "var(--color-error)",
    border: "1px solid var(--color-error)",
    background: "var(--bg1)",
  },
  waiting: {
    color: "var(--color-coral)",
    border: "1px solid var(--color-coral)",
    background: "var(--bg1)",
  },
  paused: {
    color: "var(--fg3)",
    border: "var(--border-hairline)",
    background: "var(--bg2)",
  },
};

const DEFAULT_STYLE: { color: string; border: string; background: string } = {
  color: "var(--fg3)",
  border: "var(--border-hairline)",
  background: "var(--bg1)",
};

export function Badge({ status, icon, children, style: userStyle, className, ...rest }: BadgeProps) {
  const variant = status ? STATUS_STYLES[status] : DEFAULT_STYLE;
  const prevStatusRef = useRef(status);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (prevStatusRef.current !== status && prevStatusRef.current !== undefined) {
      setFading(true);
      // Clear the fade class after one frame (~16ms) so animation plays once
      const id = requestAnimationFrame(() => {
        setFading(false);
      });
      return () => cancelAnimationFrame(id);
    }
    prevStatusRef.current = status;
    return undefined;
  }, [status]);

  // Also update ref on non-fading renders
  useEffect(() => {
    prevStatusRef.current = status;
  });

  const baseStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--space-xxs)",
    font: "var(--fs-mono)/1.4 var(--font-mono)",
    letterSpacing: "0.28px",
    textTransform: "uppercase",
    padding: "var(--space-xxs) var(--space-sm)",
    paddingLeft: "var(--space-xs)",
    paddingRight: "var(--space-xs)",
    borderRadius: "var(--radius-full)",
    color: variant.color,
    border: variant.border,
    background: variant.background,
    ...userStyle,
  };

  const fadeClass = fading ? "motion__status-fade" : "";
  const combinedClassName = [className, fadeClass].filter(Boolean).join(" ") || undefined;

  return (
    <span role="status" style={baseStyle} className={combinedClassName} {...rest}>
      {icon && <Icon name={icon} size={12} aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Backwards-compatible alias: maps `kind` prop to `status`. */
export interface LegacyBadgeProps extends Omit<BadgeProps, "status"> {
  kind: BadgeProps["status"];
}

export function BadgeByKind({ kind, ...rest }: LegacyBadgeProps) {
  return <Badge status={kind} {...rest} />;
}

export default Badge;
